import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import asyncService from '../../../datahub/asyncService';
import { showSnackbar } from '../../../main-deprecated/views/app';
import selectUser from '../../store/selectors/appSelector';

import { SAVE_STATUS } from './constants';
import { getId } from './helpers';
import { patchPositionTemplateDetail } from './positionTemplateDetailReducer';
import { selectPositionTemplateDetail } from './positionTemplateDetailSelectors';

const SAVED_STATUS_DURATION_MS = 2500;

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const getIn = (object, path) => path.reduce((acc, key) => acc?.[key], object);

const setIn = (object, [key, ...rest], value) => ({
  ...object,
  [key]: rest.length ? setIn(object?.[key], rest, value) : value,
});

const getChangedPaths = (current, patch, prefix = []) =>
  Object.keys(patch).flatMap((key) => {
    const path = [...prefix, key];
    const next = patch[key];
    const prev = current?.[key];
    if (isPlainObject(next) && isPlainObject(prev)) return getChangedPaths(prev, next, path);
    return next === prev ? [] : [path];
  });

const usePositionTemplateFieldSave = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { token, refreshToken } = useSelector(selectUser) || {};
  const templateId = getId(useSelector(selectPositionTemplateDetail));
  const [statusByKey, setStatusByKey] = useState({});

  const savesByKey = useRef({});
  const savedTimers = useRef({});
  const session = useRef(0);

  useEffect(() => () => Object.values(savedTimers.current).forEach(clearTimeout), []);

  useEffect(() => {
    savesByKey.current = {};
    setStatusByKey({});
    return () => {
      session.current += 1;
    };
  }, [templateId]);

  const setStatus = useCallback((key, status) => {
    clearTimeout(savedTimers.current[key]);
    setStatusByKey((prev) => ({ ...prev, [key]: status }));
    if (status === SAVE_STATUS.SAVED) {
      savedTimers.current[key] = setTimeout(() => {
        setStatusByKey((prev) => ({ ...prev, [key]: undefined }));
      }, SAVED_STATUS_DURATION_MS);
    }
  }, []);

  const saveField = useCallback(
    ({ key, fields, patch, persist = true, request, fromResponse, onError }) => {
      const getTemplate = () => selectPositionTemplateDetail(store.getState());
      const current = getTemplate();
      const savedTemplateId = getId(current);
      const savedSession = session.current;
      const changedPaths = getChangedPaths(current, patch);

      dispatch(patchPositionTemplateDetail(patch));

      if (!persist) {
        setStatus(key, SAVE_STATUS.LOCAL_ONLY);
        return;
      }

      const save = savesByKey.current[key]?.pending
        ? savesByKey.current[key]
        : { latestId: 0, lastSucceededId: 0, failedId: 0, pending: 0, baseline: new Map() };
      savesByKey.current[key] = save;
      save.latestId += 1;
      save.pending += 1;
      const requestId = save.latestId;
      changedPaths.forEach((path) => {
        const pathKey = JSON.stringify(path);
        if (!save.baseline.has(pathKey)) {
          save.baseline.set(pathKey, { path, value: getIn(current, path) });
        }
      });

      const isLatest = () => savesByKey.current[key] === save && save.latestId === requestId;
      const isSameVisit = () =>
        session.current === savedSession && getId(getTemplate()) === savedTemplateId;
      const isRolledBack = () =>
        savesByKey.current[key] === save && save.failedId === save.latestId;

      const applyPaths = (entries) => {
        const updated = entries.reduce(
          (acc, { path, value }) => setIn(acc, path, value),
          getTemplate()
        );
        const touchedFields = new Set(entries.map(({ path }) => path[0]));
        dispatch(
          patchPositionTemplateDetail(
            Object.fromEntries([...touchedFields].map((field) => [field, updated[field]]))
          )
        );
      };

      setStatus(key, SAVE_STATUS.SAVING);
      asyncService({
        httpMethod: 'PATCH',
        route: 'position-templates',
        data: { id: savedTemplateId, fields },
        ...request,
        token,
        refreshToken,
        dispatch,
        onSuccess: (response) => {
          save.pending -= 1;
          if (!isSameVisit()) return;
          const isLatestSave = isLatest();
          const isNewest = requestId > save.lastSucceededId;
          const restoresRollback = !isLatestSave && isNewest && isRolledBack();
          if (restoresRollback) {
            applyPaths(changedPaths.map((path) => ({ path, value: getIn(patch, path) })));
          }
          if (isLatestSave || restoresRollback) {
            const synced = fromResponse?.(response?.data?.document);
            if (synced) dispatch(patchPositionTemplateDetail(synced));
            setStatus(key, SAVE_STATUS.SAVED);
          }
          if (isNewest) {
            save.lastSucceededId = requestId;
            const saved = isLatestSave || restoresRollback ? getTemplate() : patch;
            changedPaths.forEach((path) => {
              save.baseline.set(JSON.stringify(path), { path, value: getIn(saved, path) });
            });
          }
        },
        onError: (error) => {
          save.pending -= 1;
          if (!isSameVisit()) {
            dispatch(showSnackbar({ message: 'error.positionTemplate.failUpdate' }));
            return;
          }
          if (!isLatest()) return;
          save.failedId = requestId;
          applyPaths([...save.baseline.values()]);
          setStatus(key, SAVE_STATUS.ERROR);
          dispatch(showSnackbar({ message: 'error.positionTemplate.failUpdate' }));
          onError?.(error);
        },
      });
    },
    [dispatch, refreshToken, setStatus, store, token]
  );

  const getStatus = useCallback((key) => statusByKey[key], [statusByKey]);

  return { saveField, getStatus };
};

export default usePositionTemplateFieldSave;
